import { connect, useSelector } from 'react-redux';
import { pipe } from 'fp-ts/function';
import { formValueSelector } from 'redux-form';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { UndoCancellationReason } from '@mews-commander/data-access';
import { commanderForm, EnumSelectField, InputField, presence, validation } from '@mews-commander/legacy-form';
import { undoReservationCancellation } from '@mews-commander/legacy-entities/src/ducks/reservations';
import { FullWidthFieldsContainer } from '@mews-commander/ui';

interface FormValues {
    undoCancellationReason: UndoCancellationReason;
    reasonDetail: string;
}

interface OwnProps {
    onSuccess: () => void;
    reservationIds: string[];
    form: string;
}

interface DispatchProps {
    onSubmit: (values: FormValues) => void;
}

export type UndoReservationCancellationFormProps = OwnProps & DispatchProps;

const undoCancellationReasonProps = {
    label: <LocalizedText textKey={TextKey.ReservationDetailReasonInputLabel} />,
    name: 'undoCancellationReason',
    required: true,
    enumClass: UndoCancellationReason,
    enumPrefix: 'UndoCancellationReason',
} as const;

const detailsProps = {
    label: <LocalizedText textKey={TextKey.DetailPlural} />,
    name: 'reasonDetail',
};
const UndoReservationCancellationFormFields = ({ form }: UndoReservationCancellationFormProps) => {
    const isReasonRequired = useSelector(
        (state: ApplicationState) =>
            formValueSelector(form)(state, 'undoCancellationReason') === UndoCancellationReason.Other
    );
    return (
        <FullWidthFieldsContainer>
            <EnumSelectField {...undoCancellationReasonProps} />
            <InputField {...detailsProps} required={isReasonRequired} />
        </FullWidthFieldsContainer>
    );
};

const getConstraints = (_props: UndoReservationCancellationFormProps, { undoCancellationReason }: FormValues) => ({
    undoCancellationReason: { presence },
    reasonDetail: undoCancellationReason === UndoCancellationReason.Other ? { presence } : {},
});

export default pipe(
    UndoReservationCancellationFormFields,
    commanderForm<FormValues, UndoReservationCancellationFormProps>({
        enableReinitialize: true,
        submitLabel: <LocalizedText textKey={TextKey.Restore} />,
        validate: validation(getConstraints),
    }),
    connect<undefined, DispatchProps, OwnProps>(
        undefined,
        (dispatch: CommanderThunkDispatch, { reservationIds, onSuccess }) => ({
            onSubmit: async (values) =>
                dispatch(undoReservationCancellation({ reservationIds, ...values })).then(onSuccess),
        })
    )
);
